import React, { useEffect } from "react";
import { Route, Switch } from "wouter";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import Logout from "./pages/Logout";
import PromotionCreate from "./pages/PromotionCreate";
import Summary from "./pages/Summary";
import ProtectedRoute from "components/ProtectedRoute/ProtectedRoute";
import { useDispatch, useSelector } from "react-redux";
import { getAndSetUserInfo, switchStore } from "store/actions/auth.actions";
import { useApiInfo, useLocationUpdate } from "hooks";
import { IState } from "store/store.interface";
import { getAppConfig } from "store/actions/config.actions";
import MyProfile from "pages/MyProfile";
import Users from "pages/Users";
import useAuthenticate from '../src/hooks/useAuthenticate'
import CartBundle from "pages/CartBundle"
import CartBundleHelper from "pages/CartBundleHelper";
import CartBundleCreate from "pages/CartBundleCreate";
import CampaignDetails from "pages/CampaignDetails";
import CreateBulkCampaign from "pages/CreateBulkCampaign";
import BulkUploadJobs from "pages/BulkUploadJobs"
import { AUTH_GET_USER, AUTH_SWITCH_STORE, AUTH_VERIFY_BOUND_STORE } from "store/store.types";
import { useLocation } from "wouter";
import { EStores } from "store/reducers/auth.reducers";

const App = () => {
  const dispatch = useDispatch();
  const [currentPath] = useLocation();

  const { selectOptions, storeId, storeIds = [] } = useSelector((state: IState) => ({
    ...state.config,
    ...state.auth,
  }));
  const needsServerStoreBind = storeIds.length > 1;

  const { isAuthenticated, getTokenFromStorage, inProgress } = useAuthenticate();
  const token = getTokenFromStorage();
  const { success: getUserSuccess } = useApiInfo(AUTH_GET_USER);
  const {
    success: switchStorePostSuccess,
    isLoading: isSwitchStorePosting,
    done: switchStorePostDone,
  } = useApiInfo(AUTH_SWITCH_STORE);
  const {
    success: switchStoreSuccess,
    isLoading: isVerifyingBoundStore,
    done: verifyBoundStoreDone,
  } = useApiInfo(AUTH_VERIFY_BOUND_STORE);
  const isSwitchStoreLoading = isSwitchStorePosting || isVerifyingBoundStore;
  const switchStoreDone = switchStorePostDone && (!switchStorePostSuccess || verifyBoundStoreDone);

  useLocationUpdate();

  useEffect(() => {
    if (!selectOptions.countries && isAuthenticated) {
      dispatch(getAppConfig());
    }
  }, [selectOptions, dispatch, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && token && !inProgress) {
      dispatch(getAndSetUserInfo(token));
    }
  }, [dispatch, isAuthenticated, token, inProgress]);

  useEffect(() => {
    if (
      isAuthenticated
      && getUserSuccess
      && storeId
      && needsServerStoreBind
      && currentPath !== "/login"
      && !switchStoreSuccess
      && !isSwitchStoreLoading
      && !switchStoreDone
    ) {
      dispatch(switchStore(storeId as EStores));
    }
  }, [
    isAuthenticated,
    getUserSuccess,
    storeId,
    storeIds,
    needsServerStoreBind,
    currentPath,
    switchStoreSuccess,
    isSwitchStoreLoading,
    switchStoreDone,
    dispatch,
  ]);

  return (
    <Switch>
      <Route path="/login" component={Login} />
      <Route path="/logout" component={Logout} />
      <ProtectedRoute path="/" component={Dashboard} />
      <ProtectedRoute path="/promotion-create" component={PromotionCreate} />
      <ProtectedRoute path="/summary" component={Summary} />
      <ProtectedRoute path="/my-profile" component={MyProfile} />
      <ProtectedRoute path="/users" component={Users} />
      <ProtectedRoute path="/cart-bundles" component={CartBundle} />
      <ProtectedRoute path="/cart-bundle-help" component={CartBundleHelper} />
      <ProtectedRoute path="/cart-bundle-create" component={CartBundleCreate} />
      <ProtectedRoute path="/cart-bundle-details" component={CampaignDetails} />
      <ProtectedRoute path="/create-bulk-campaign" component={CreateBulkCampaign} />
      <ProtectedRoute path="/bulk-campaign-jobs" component={BulkUploadJobs} />
      <ProtectedRoute path="/recurring-coupons" component={Dashboard} />
    </Switch>
  );
};

export default App;
